import { Divider, Stack, Typography } from '@mui/material';
import { noop } from 'lodash';
import { useWatch } from 'react-hook-form';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { InsurancePolicyForm } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.UI';
import { COLORS } from '@/styles/tokens/colors';

import { useUpdateInsuranceFormAfterSelection } from '../hooks/useUpdateInsuranceFormAfterSelection';
import { useHypotheticalILITModalContext } from '../HypotheticalILITModal.context';
import {
  HypotheticalILITFormModalShape,
  HypotheticalILITFormModalShapePaths,
  HypotheticalILITInsurancePolicyType,
} from '../HypotheticalILITModal.types';

export function HypotheticalILITPolicyDetailsBody() {
  const { householdId, insurancePolicyOptions } =
    useHypotheticalILITModalContext();
  const { control } = useFormContext<HypotheticalILITFormModalShape>();

  const [policyType, existingInsurancePolicyId] = useWatch({
    control,
    name: ['_hypotheticalInsurancePolicyType', 'existingInsurancePolicyId'],
  });

  // repopulate the form when the existing insurance policy ID changes
  useUpdateInsuranceFormAfterSelection();

  const hasPolicyType =
    (policyType === HypotheticalILITInsurancePolicyType.Existing &&
      !!existingInsurancePolicyId) ||
    policyType === HypotheticalILITInsurancePolicyType.New;

  const isDisabled =
    policyType === HypotheticalILITInsurancePolicyType.Existing;

  return (
    <Card variant="filled">
      <Stack>
        <Card variant="filled" sx={{ px: 2, pt: 3, pb: hasPolicyType ? 0 : 3 }}>
          <FormLayoutRow>
            <FormLayoutItem>
              <Stack spacing={0.5}>
                <Typography variant="title-l">Policy details</Typography>
                <Typography variant="body-xs">
                  Specify details for the existing policy being transferred or
                  the new hypothetical policy being created. The cash value of
                  existing policies will be treated as a taxable gift.
                </Typography>
              </Stack>
            </FormLayoutItem>
          </FormLayoutRow>
          <FormLayoutRow>
            <FormLayoutItem>
              <FormAwareRadioGroup<HypotheticalILITFormModalShape>
                control={control}
                fieldName={
                  '_hypotheticalInsurancePolicyType' as const satisfies HypotheticalILITFormModalShapePaths
                }
                options={[
                  {
                    label: 'Existing policy',
                    value: HypotheticalILITInsurancePolicyType.Existing,
                  },
                  {
                    label: 'Create hypothetical policy',
                    value: HypotheticalILITInsurancePolicyType.New,
                  },
                ]}
                row
                hideLabel
                required
              />
            </FormLayoutItem>
          </FormLayoutRow>
          {(policyType === HypotheticalILITInsurancePolicyType.Existing ||
            !policyType) && (
            <FormLayoutRow>
              <FormLayoutItem>
                <FormAwareSelectInput<HypotheticalILITFormModalShape>
                  control={control}
                  fieldName={
                    'existingInsurancePolicyId' as const satisfies HypotheticalILITFormModalShapePaths
                  }
                  label="Existing policy"
                  options={insurancePolicyOptions}
                  required
                />
              </FormLayoutItem>
            </FormLayoutRow>
          )}
          {hasPolicyType && (
            <FormLayoutRow>
              <FormLayoutItem>
                <Divider
                  sx={{
                    borderColor: COLORS.GRAY[300],
                  }}
                  variant="fullWidth"
                />
              </FormLayoutItem>
            </FormLayoutRow>
          )}
        </Card>
        {hasPolicyType && (
          <Stack sx={{ p: 2 }}>
            <FormFieldsDisabledProvider isDisabled={isDisabled}>
              <InsurancePolicyForm
                policyIndex={0}
                householdId={householdId}
                variant="default"
                remove={noop}
                isHypothetical
                isCreateHypothetical={
                  policyType === HypotheticalILITInsurancePolicyType.New
                }
                additionalItemRendererProps={{
                  hideDeleteButton: true,
                }}
              />
            </FormFieldsDisabledProvider>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
